import './search-form.scss'

import React, { useEffect, useState, useCallback } from 'react'
import { PrimaryButton, TertiaryButton } from '@entur/button'
import { Heading1, Label } from '@entur/typography'
import { GridContainer, GridItem } from '@entur/grid'
import { AddIcon, ResetIcon } from '@entur/icons'
import { space } from '@entur/tokens'
import { FilterChip } from '@entur/chip'
import { useNavigate, useLocation } from 'react-router'
import { useHotkeys } from 'react-hotkeys-hook'
import { FormField, StringFormField, ValidationFormField } from '../../utils/form-utils'
import { tolLocalDateFormat } from '../../utils/date-utils'
import { useStateWithSessionStorage } from '../../hooks/common-hooks'
import { FullPageContrastWrapper } from '../layout/full-page-contrast-wrapper'
import ProductsChoiceField from './products-choice-field'
import {
  dateOfBirthFormField,
  employeeNoFormField,
  firstNameFormField,
  postalCodeFormField,
  surnameFormField,
} from '../../utils/common-form-fields'
import { trackEvent } from '../../utils/tracking-utils'
import { ValidatableTextField } from '../form/validatable-text-field'
import ValidatableDatePicker from '../form/validatable-date-picker'
import CompanyDropdown, { companyFormField } from '../form/company-dropdown'
import PostalField from '../form/postal-field'
import RuleDropdown, { ruleFormField } from '../form/rule-dropdown'
import { HistoryState } from '../../types'
import { useClientInfo } from '../../contexts/client-info-context'

type State = {
  firstName: ValidationFormField<string>
  surname: ValidationFormField<string>
  employeeNo: ValidationFormField<string>
  postalCode: ValidationFormField<string>
  city: StringFormField
  companyId: ValidationFormField<number | undefined>
  dateOfBirth: ValidationFormField<Date | undefined>
  productName: string
  ruleId: FormField<string | undefined>
  hasDraft: boolean
  hasPendingTicketRights: boolean
  [key: string]: unknown
}
const emptyValue = { value: '', variant: '', feedback: '' }
const initialState: State = {
  firstName: firstNameFormField('', false),
  surname: surnameFormField('', false),
  dateOfBirth: dateOfBirthFormField(undefined, false),
  productName: '',
  employeeNo: employeeNoFormField(undefined, false),
  postalCode: postalCodeFormField('', false),
  city: { fieldName: 'city', displayName: 'Poststed', ...emptyValue },
  companyId: companyFormField(undefined, false),
  ruleId: ruleFormField(undefined, 'Regel'),
  hasDraft: false,
  hasPendingTicketRights: false,
}

const getQueryVal = (value: unknown): string | undefined => {
  const valueToCheck =
    typeof value === 'object' && value && (value as FormField<unknown>).value !== undefined
      ? (value as FormField<string | undefined>).value
      : value
  if (valueToCheck instanceof Date) return tolLocalDateFormat(valueToCheck)
  if (typeof valueToCheck === 'boolean') return valueToCheck ? 'true' : undefined
  if (typeof valueToCheck == 'undefined' || typeof valueToCheck === 'string') return valueToCheck
  if (typeof valueToCheck == 'undefined' || typeof valueToCheck === 'number') return valueToCheck?.toString()
  return undefined
}

const makeQuery = (state: State): string => {
  let query = '?'
  for (const [key, value] of Object.entries(state)) {
    const queryVal = getQueryVal(value)
    if (queryVal !== undefined && queryVal !== '') {
      query += `${key}=${queryVal}&`
    }
    if (key === 'hasDraft' && value) {
      query += 'orderBy=draftHireEndDate,surname&orderDirection=asc&'
    }
  }
  return query.substring(0, query.length - 1)
}

type SearchFormProps = {
  performSearch: (query: string) => void
  isLoading: boolean
}
const SearchForm: React.FC<SearchFormProps> = (props) => {
  const [state, setState] = useStateWithSessionStorage<State>('search-form-state', initialState)
  const navigate = useNavigate()
  const location = useLocation()
  const locState = location.state as HistoryState | undefined
  const { hasSensitiveAccess } = useClientInfo()
  const [resetSearch, setReset] = useState(location && locState ? locState.resetSearch : false)

  const resetInput = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    trackEvent('Reset-Search', { category: 'Search' })
    event?.preventDefault()
    setState(initialState)
  }
  const resetInputCallback = useCallback(resetInput, [setState])

  const performSearch = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    props.performSearch(makeQuery(state))
    trackEvent('Perform-Search', { category: 'Search' })
  }

  useHotkeys(
    'alt+u',
    (event) => {
      event.preventDefault()
      resetInput()
      const focusElement = document.querySelector(`input[name=${state.dateOfBirth.fieldName}]`) as HTMLInputElement
      if (focusElement) {
        focusElement.focus()
      }
    },
    // Accept all html elements (even input.)
    {
      enabled: true,
    }
  )

  // Overrides clear fields on enter
  useEffect(() => {
    document.getElementById('search__form')?.addEventListener('keypress', (event) => {
      if (event.keyCode === 13) {
        event.preventDefault()
        ;(document.getElementById('submit-btn') as HTMLFormElement)?.click()
      }
    })
  }, [])

  useEffect(() => {
    if (resetSearch) resetInputCallback()
    setReset(false)
  }, [resetSearch, resetInputCallback, setReset])

  return (
    <FullPageContrastWrapper>
      <div className="search-form">
        <form id="search__form" onSubmit={(e) => performSearch(e)}>
          <GridContainer spacing="medium" rowSpacing="large" className="search__header-wrapper">
            <GridItem small={12} medium={8}>
              <Heading1 margin="none" className="search-form__header">
                Søk opp rettighetshaver
              </Heading1>
            </GridItem>
            <GridItem small={12} medium={4} className="search-form__button-wrapper">
              {hasSensitiveAccess === true && (
                <TertiaryButton style={{ marginRight: space.medium }} onClick={() => navigate('/nyansatt')}>
                  <AddIcon />
                  Ny rettighetseier
                </TertiaryButton>
              )}
              <TertiaryButton
                id="search-form__reset-button"
                data-cy="search-form-reset-button"
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => resetInput(e)}
              >
                <ResetIcon />
                Nullstill søk
              </TertiaryButton>
            </GridItem>
            <GridItem small={12} medium={3}>
              <ValidatableDatePicker
                date={state.dateOfBirth}
                update={(value: ValidationFormField<Date | undefined>) =>
                  setState({ ...state, dateOfBirth: value })
                }
              />
            </GridItem>
            <GridItem small={12} medium={3}>
              <ValidatableTextField
                field={state.surname}
                update={(value: ValidationFormField<string>) => setState({ ...state, surname: value })}
              />
            </GridItem>
            <GridItem small={12} medium={3}>
              <ValidatableTextField
                field={state.firstName}
                update={(value: ValidationFormField<string>) => setState({ ...state, firstName: value })}
              />
            </GridItem>
            <GridItem small={12} medium={3}>
              <PostalField
                postalCode={state.postalCode}
                update={(value: ValidationFormField<string>) => setState({ ...state, postalCode: value })}
              />
            </GridItem>
            <GridItem small={12} medium={3}>
              <ValidatableTextField
                field={state.employeeNo}
                update={(value: ValidationFormField<string>) => setState({ ...state, employeeNo: value })}
              />
            </GridItem>
            <GridItem data-cy="company-dropdown" small={12} medium={3}>
              <CompanyDropdown
                company={state.companyId}
                placeholder="Alle selskap"
                update={(value: ValidationFormField<number | undefined>) =>
                  setState({ ...state, companyId: value })
                }
              />
            </GridItem>
            {hasSensitiveAccess === true && (
              <GridItem data-cy="rules-dropdown" small={12} medium={6} large={6}>
                <RuleDropdown
                  rules={state.ruleId}
                  placeholder="Regel"
                  update={(value) => setState({ ...state, ruleId: value })}
                />
              </GridItem>
            )}
            <GridItem small={12} medium={12} large={12}>
              <div style={{ float: 'left' }}>
                <Label>Kategori</Label>
                <ProductsChoiceField
                  productNames={state.productName}
                  setProductsName={(value) => setState({ ...state, productName: value })}
                />
              </div>

              <div style={{ float: 'left' }}>
                <Label>Status</Label>
                <div>
                  <FilterChip
                    style={{ float: 'left', marginRight: space.medium }}
                    checked={state.hasDraft}
                    value="hasDraft"
                    onChange={() => setState({ ...state, hasDraft: !state.hasDraft })}
                  >
                    Har utkast
                  </FilterChip>
                  <FilterChip
                    style={{ float: 'left' }}
                    checked={state.hasPendingTicketRights}
                    value="hasPendingTicketRights"
                    onChange={() => setState({ ...state, hasPendingTicketRights: !state.hasPendingTicketRights })}
                  >
                    Usynkroniserte rettigheter
                  </FilterChip>
                </div>
              </div>
            </GridItem>
            <GridItem small={undefined} medium={6} large={9} />
            <GridItem small={12} medium={6} large={3}>
              <PrimaryButton
                id="submit-btn"
                className="search-form__search-button"
                type="submit"
                loading={props.isLoading}
                data-cy="search-submit-button"
              >
                Søk etter rettighetshaver
              </PrimaryButton>
            </GridItem>
          </GridContainer>
        </form>
      </div>
    </FullPageContrastWrapper>
  )
}

export default SearchForm
